/**
 * VoiceVisualizerTest- 测试最终解决方案
 * 使用 useVoiceVisualizerWrapper 解决快速点击问题
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, VStack, Text, Box, HStack } from '@chakra-ui/react';
import { useVoiceVisualizerWrapper } from '../../../../hooks/v3/audio/useVoiceVisualizerWrapper';
import { useFrontendLogger } from '../../../../atoms/v3/debugAtoms';

const VoiceVisualizerTestMinimal: React.FC = () => {
  const logger = useFrontendLogger();
  const [testCount, setTestCount] = useState(0);
  const [lastRecordingInfo, setLastRecordingInfo] = useState<{
    size: number;
    type: string;
    timestamp: number;
  } | null>(null);
  const [lastTranscription, setLastTranscription] = useState<string | null>(null);
  const [enableSTT, setEnableSTT] = useState(false);
  const lastProcessedBlobRef = useRef<Blob | null>(null);

  // 录音回调
  const handleRecordingStart = useCallback(() => {
    logger.info('VoiceVisualizerTestMinimal: 📢 Recording started');
  }, [logger]);

  const handleRecordingStop = useCallback(() => {
    logger.info('VoiceVisualizerTestMinimal: ⏹️ Recording stopped');
  }, [logger]);

  const handleRecordingError = useCallback((error: Error) => {
    logger.error('VoiceVisualizerTestMinimal: ❌ Recording error', error);
  }, [logger]);

  // 🔥 新增：STT转录完成回调
  const handleTranscriptionComplete = useCallback((text: string) => {
    logger.info('VoiceVisualizerTestMinimal: 🎯 STT转录完成', { text });
    setLastTranscription(text);
    console.log('🎯 STT转录结果:', text);
  }, [logger]);

  // 使用最终解决方案
  const {
    startRecording,
    stopRecording,
    isRecordingInProgress,
    recordedBlob,
    recordingTime,
    formattedRecordingTime,
    mediaRecorder
  } = useVoiceVisualizerWrapper({
    onStartRecording: handleRecordingStart,
    onStopRecording: handleRecordingStop,
    onErrorPlayingAudio: handleRecordingError,
    onTranscriptionComplete: handleTranscriptionComplete, // 🔥 新增：STT回调
    enableSTT: enableSTT, // 🔥 新增：启用STT功能
  });

  // 监听录音完成
  useEffect(() => {
    if (recordedBlob && recordedBlob.size > 0 && recordedBlob !== lastProcessedBlobRef.current) {
      // 更新引用，避免重复处理
      lastProcessedBlobRef.current = recordedBlob;

      setLastRecordingInfo({
        size: recordedBlob.size,
        type: recordedBlob.type,
        timestamp: Date.now()
      });

      logger.info('VoiceVisualizerTestMinimal: Recording completed', {
        size: recordedBlob.size,
        type: recordedBlob.type
      });
    }
  }, [recordedBlob, logger]);

  // 快速点击测试 - 最终解决方案
  const handleQuickClickTest = useCallback(() => {
    const count = testCount + 1;
    setTestCount(count);
    logger.info(`VoiceVisualizerTestMinimal: 🚀 Quick Click Test ${count}: 使用最终解决方案`);

    // 立即触发（模拟快速点击）
    logger.info(`VoiceVisualizerTestMinimal: Test ${count} - Calling startRecording()`);
    startRecording();

    logger.info(`VoiceVisualizerTestMinimal: Test ${count} - Immediately calling stopRecording()`);
    stopRecording();

    // 检查结果
    setTimeout(() => {
      const finalState = {
        isRecordingInProgress,
        recordingTime,
        hasRecordedBlob: !!recordedBlob,
        mediaRecorderState: mediaRecorder?.state || 'null'
      };
      
      console.log(`🚀 Final solution test ${count} result:`, finalState);
      logger.info(`VoiceVisualizerTestMinimal: 🚀 Test ${count} result:`, finalState);

      if (finalState.isRecordingInProgress || finalState.mediaRecorderState === 'recording') {
        logger.error(`VoiceVisualizerTestMinimal: ❌ Test ${count} FAILED - recording still active!`);
      } else {
        logger.info(`VoiceVisualizerTestMinimal: ✅ Test ${count} SUCCESS - recording stopped!`);
      }
    }, 2000);
  }, [startRecording, stopRecording, testCount, logger, isRecordingInProgress, recordingTime, recordedBlob, mediaRecorder]);

  // 正常录音测试
  const handleNormalRecordingTest = useCallback(async () => {
    const count = testCount + 1;
    setTestCount(count);
    logger.info(`VoiceVisualizerTestMinimal: ⏱️ Normal Recording Test ${count}`);

    try {
      logger.info(`VoiceVisualizerTestMinimal: Test ${count} - Starting normal recording`);
      startRecording();
      
      // 等待 2 秒
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      logger.info(`VoiceVisualizerTestMinimal: Test ${count} - Stopping after 2 seconds`);
      stopRecording();
      
      logger.info(`VoiceVisualizerTestMinimal: Test ${count} - Normal recording test completed`);
    } catch (error) {
      logger.error(`VoiceVisualizerTestMinimal: ❌ Normal Recording Test ${count} failed`, error);
    }
  }, [startRecording, stopRecording, testCount, logger]);

  // 重置状态
  const handleReset = useCallback(() => {
    setTestCount(0);
    setLastRecordingInfo(null);
    logger.info('VoiceVisualizerTestMinimal: State reset');
  }, [logger]);

  // 复制状态到剪贴板
  const copyStatus = useCallback(async () => {
    const status = {
      isRecordingInProgress,
      recordingTime,
      formattedRecordingTime,
      hasRecordedBlob: !!recordedBlob,
      mediaRecorderState: mediaRecorder?.state || 'null',
      testCount
    };
    const statusText = JSON.stringify(status, null, 2);

    try {
      await navigator.clipboard.writeText(statusText);
      logger.info('VoiceVisualizerTestMinimal: ✅ Status copied to clipboard');
    } catch (error) {
      logger.error('VoiceVisualizerTestMinimal: ❌ Failed to copy status', error);
    }
  }, [isRecordingInProgress, recordingTime, formattedRecordingTime, recordedBlob, mediaRecorder, testCount, logger]);

  return (
    <VStack spacing={6} p={6} align="stretch" maxW="800px" mx="auto">
      <Text fontSize="2xl" fontWeight="bold" textAlign="center">
        最终解决方案测试
      </Text>

      {/* 当前状态显示 */}
      <Box p={4} bg="gray.50" borderRadius="md">
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold">当前状态:</Text>
          <Button size="sm" onClick={copyStatus} colorScheme="blue">
            复制状态
          </Button>
        </HStack>
        <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify({
            isRecordingInProgress,
            recordingTime,
            formattedRecordingTime,
            hasRecordedBlob: !!recordedBlob,
            mediaRecorderState: mediaRecorder?.state || 'null',
            testCount
          }, null, 2)}
        </pre>
      </Box>

      {/* STT控制 */}
      <Box p={4} bg="blue.50" borderRadius="md">
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold">🎯 STT功能测试:</Text>
          <Button
            size="sm"
            colorScheme={enableSTT ? "green" : "gray"}
            onClick={() => setEnableSTT(!enableSTT)}
          >
            {enableSTT ? "✅ STT已启用" : "❌ STT已禁用"}
          </Button>
        </HStack>
        <Text fontSize="sm" color="blue.800">
          启用后，录音完成时会自动调用新的STT API进行转录
        </Text>
      </Box>

      {/* STT转录结果 */}
      {lastTranscription && (
        <Box p={4} bg="purple.50" borderRadius="md">
          <Text fontWeight="bold" mb={2}>🎯 最新转录结果:</Text>
          <Text fontSize="sm" p={2} bg="white" borderRadius="md" border="1px solid" borderColor="purple.200">
            {lastTranscription}
          </Text>
        </Box>
      )}

      {/* 最后录音信息 */}
      {lastRecordingInfo && (
        <Box p={4} bg="green.50" borderRadius="md">
          <Text fontWeight="bold" mb={2}>最后录音信息:</Text>
          <Text fontSize="sm">
            大小: {lastRecordingInfo.size} bytes<br />
            类型: {lastRecordingInfo.type}<br />
            时间: {new Date(lastRecordingInfo.timestamp).toLocaleTimeString()}
          </Text>
        </Box>
      )}

      {/* 紧急控制按钮 */}
      <HStack spacing={2} wrap="wrap" mb={4}>
        <Button
          colorScheme="red"
          size="lg"
          onClick={stopRecording}
          disabled={!isRecordingInProgress}
        >
          🛑 紧急停止录音
        </Button>
        <Button
          colorScheme="green"
          onClick={startRecording}
          disabled={isRecordingInProgress}
        >
          🎙️ 手动开始录音
        </Button>
      </HStack>

      {/* 测试按钮 */}
      <HStack spacing={3} wrap="wrap">
        <Button
          colorScheme="red"
          onClick={handleQuickClickTest}
          size="lg"
        >
          🚀 快速点击测试
        </Button>
        <Button
          colorScheme="blue"
          onClick={handleNormalRecordingTest}
          size="lg"
        >
          ⏱️ 正常录音测试 (2s)
        </Button>
        <Button
          colorScheme="purple"
          onClick={() => {
            setEnableSTT(true);
            handleNormalRecordingTest();
          }}
          size="lg"
        >
          🎯 STT录音测试 (2s)
        </Button>
        <Button
          colorScheme="gray"
          onClick={handleReset}
        >
          🔄 重置状态
        </Button>
      </HStack>

      {/* 说明 */}
      <Box p={4} bg="green.50" borderRadius="md">
        <Text fontSize="sm" color="green.800">
          <strong>🎉 最终解决方案 + STT功能</strong><br />
          <strong>核心原理</strong>：直接操作 MediaRecorder.stop()，绕过库的状态检查<br />
          <strong>关键技术</strong>：监听 MediaRecorder.state 而不是 React 状态<br />
          <strong>最小时长</strong>：500ms 防止过短录音<br />
          <strong>超时保护</strong>：1秒超时避免无限等待<br />
          <strong>🎯 STT功能</strong>：使用新的GPT-4o-mini-transcribe模型和优化prompt<br />
          <br />
          <strong>测试重点</strong>：快速点击测试应该不再导致永久录音状态，STT测试应该在控制台显示转录结果
        </Text>
      </Box>
    </VStack>
  );
};

export default VoiceVisualizerTestMinimal;
